// Rule Engine v6.0 — Deterministic decision logic + Pricing Engine
// Ensures engineering correctness and generates budget estimates

import { RFQState } from './rfqConfig';

export interface EquipmentItem {
  name: string;
  specification: string;
  category: 'primary' | 'safety' | 'support' | 'logistics';
  dailyRate: number; // USD per day (internal only)
}

export interface PricingEstimate {
  equipmentCostRange: [number, number];
  crewCostRange: [number, number];
  mobilizationRange: [number, number];
  contingencyRange: [number, number];
  totalRange: [number, number];
}

export interface RuleEngineOutput {
  packageLevel: string;
  equipmentList: EquipmentItem[];
  flags: string[];
  serviceModelNote: string;
  technicalNotes: string[];
  pricing: PricingEstimate;
}

// ─── PRICING CONSTANTS (INTERNAL) ───────────────────────────────────────────

const CREW_RATES = {
  supervisor: 850, // USD/day
  operator: 450, // USD/day
  hse: 600, // USD/day
};

const MOBILIZATION_COSTS = {
  local: { min: 5000, max: 12000 },
  remote: { min: 15000, max: 35000 },
  crossBorder: { min: 30000, max: 65000 },
};

// ─── DURATION MULTIPLIER ─────────────────────────────────────────────────────

function getDurationDays(state: RFQState): number {
  if (state.duration === 'short-term') return 14;
  if (state.duration === 'long-term') return 60;
  return 30; // default
}

// ─── RULE ENGINE ─────────────────────────────────────────────────────────────

export function runRuleEngine(state: RFQState): RuleEngineOutput {
  const equipment: EquipmentItem[] = [];
  const flags: string[] = [];
  const technicalNotes: string[] = [];
  let packageLevel = 'Standard Package';

  // ─── BASE LOGIC (Well Testing) ───────────────────────────────────────────
  if (state.service === 'surface-well-testing' || state.service === 'pressure-control') {
    equipment.push(
      { name: 'Choke Manifold', specification: '10K PSI', category: 'primary', dailyRate: 1200 },
      { name: 'Test Separator', specification: 'Standard', category: 'primary', dailyRate: 1800 },
      { name: 'Flare System', specification: 'Standard Burner', category: 'primary', dailyRate: 800 },
      { name: 'Flowlines', specification: '4" HP Lines', category: 'primary', dailyRate: 400 },
      { name: 'Instrumentation', specification: 'P&T Gauges + Flow Meters', category: 'primary', dailyRate: 600 },
    );
    technicalNotes.push('Base well testing package loaded with standard configuration');
  }

  if (state.service === 'stimulation-nitrogen') {
    equipment.push(
      { name: 'HP Nitrogen Unit', specification: '15K PSI', category: 'primary', dailyRate: 2200 },
      { name: 'Frac Tank', specification: '500 BBL', category: 'primary', dailyRate: 350 },
      { name: 'HP Iron & Lines', specification: '15K PSI Rated', category: 'primary', dailyRate: 500 },
      { name: 'Chemical Injection Skid', specification: 'Variable Rate', category: 'primary', dailyRate: 450 },
    );
    technicalNotes.push('Stimulation package configured for nitrogen operations');
  }

  if (state.service === 'pumping-hydrotesting' || state.service === 'pumping-solutions') {
    equipment.push(
      { name: 'HP Pump Unit', specification: '10K PSI', category: 'primary', dailyRate: 1500 },
      { name: 'Test Manifold', specification: 'Hydro-test Rated', category: 'primary', dailyRate: 600 },
      { name: 'Recording Equipment', specification: 'Chart Recorder + Digital', category: 'primary', dailyRate: 400 },
      { name: 'HP Lines & Connections', specification: 'Tested & Certified', category: 'primary', dailyRate: 350 },
    );
    technicalNotes.push('Pumping/hydrotesting configuration loaded');
  }

  if (state.service === 'chemicals-supply') {
    equipment.push(
      { name: 'Chemical Storage Tanks', specification: 'Bunded & Certified', category: 'primary', dailyRate: 200 },
      { name: 'Dosing System', specification: 'Automated Injection', category: 'primary', dailyRate: 350 },
      { name: 'Mixing Equipment', specification: 'Industrial Grade', category: 'primary', dailyRate: 250 },
    );
    technicalNotes.push('Chemical supply and injection system configured');
  }

  if (state.service === 'fabrication-skid') {
    equipment.push(
      { name: 'Fabrication Workshop', specification: 'Certified Facility', category: 'primary', dailyRate: 2500 },
      { name: 'Welding Equipment', specification: 'ASME Qualified', category: 'primary', dailyRate: 800 },
      { name: 'QC & Testing', specification: 'NDT + Hydro-test', category: 'primary', dailyRate: 600 },
    );
    technicalNotes.push('Fabrication & skid assembly scope identified');
  }

  if (state.service === 'logistics-transport') {
    equipment.push(
      { name: 'Heavy Transport Fleet', specification: 'Lowbed + Flatbed', category: 'logistics', dailyRate: 1200 },
      { name: 'Crane Support', specification: '50T Mobile Crane', category: 'logistics', dailyRate: 1500 },
      { name: 'Rigging Equipment', specification: 'Certified Slings & Shackles', category: 'logistics', dailyRate: 300 },
    );
    technicalNotes.push('Logistics and transportation package configured');
  }

  if (state.service === 'engineering-services') {
    equipment.push(
      { name: 'Engineering Design', specification: 'Process & Mechanical', category: 'primary', dailyRate: 2000 },
      { name: 'Project Management', specification: 'Dedicated PM', category: 'support', dailyRate: 1200 },
      { name: 'Documentation', specification: 'ITP, P&ID, Datasheets', category: 'support', dailyRate: 400 },
    );
    technicalNotes.push('Engineering services scope configured');
  }

  if (state.service === 'ai-systems') {
    equipment.push(
      { name: 'Data Acquisition System', specification: 'Real-time Monitoring', category: 'primary', dailyRate: 1500 },
      { name: 'AI Analytics Platform', specification: 'Predictive Maintenance', category: 'primary', dailyRate: 800 },
      { name: 'Sensor Array', specification: 'IoT Field Sensors', category: 'primary', dailyRate: 600 },
    );
    technicalNotes.push('AI & automation systems configured');
  }

  if (state.service === 'spare-parts') {
    equipment.push(
      { name: 'Parts Inventory Package', specification: 'As per Client BOM', category: 'primary', dailyRate: 500 },
      { name: 'Consumables Kit', specification: 'Seals, Gaskets, O-rings', category: 'primary', dailyRate: 200 },
    );
    technicalNotes.push('Spare parts and consumables package identified');
  }

  if (state.service === 'solid-control') {
    equipment.push(
      { name: 'Shale Shaker', specification: 'High-G Force', category: 'primary', dailyRate: 900 },
      { name: 'Centrifuge', specification: 'Decanting Type', category: 'primary', dailyRate: 1100 },
      { name: 'Waste Skip', specification: '20m³ Capacity', category: 'primary', dailyRate: 200 },
      { name: 'Transfer Pump', specification: 'Centrifugal', category: 'primary', dailyRate: 350 },
    );
    technicalNotes.push('Solid control and waste management system configured');
  }

  if (state.service === 'downhole-fiber') {
    equipment.push(
      { name: 'Fiber Optic Cable', specification: 'Downhole Rated', category: 'primary', dailyRate: 1800 },
      { name: 'DTS/DAS System', specification: 'Distributed Sensing', category: 'primary', dailyRate: 2500 },
      { name: 'Surface Interrogator', specification: 'Real-time Processing', category: 'primary', dailyRate: 1200 },
    );
    technicalNotes.push('Downhole fiber optics sensing system configured');
  }

  if (state.service === 'flame-detection') {
    equipment.push(
      { name: 'Flame Detector Array', specification: 'UV/IR Dual Spectrum', category: 'safety', dailyRate: 600 },
      { name: 'Fire Suppression System', specification: 'Automatic Deluge', category: 'safety', dailyRate: 900 },
      { name: 'Control Panel', specification: 'SIL-2 Rated', category: 'safety', dailyRate: 400 },
    );
    technicalNotes.push('Flame detection and suppression system configured');
  }

  if (state.service === 'equipment-rental') {
    equipment.push(
      { name: 'Generator Set', specification: 'As per requirement', category: 'primary', dailyRate: 800 },
      { name: 'Compressor', specification: 'As per requirement', category: 'primary', dailyRate: 700 },
      { name: 'Light Tower', specification: '4-Head LED', category: 'support', dailyRate: 150 },
    );
    technicalNotes.push('Equipment rental package — to be specified per client requirement');
  }

  // ─── PRESSURE LOGIC ──────────────────────────────────────────────────────
  const pressure = parseFloat(state.wellheadPressure) || 0;
  if (pressure > 0) {
    if (pressure <= 5000) {
      packageLevel = 'Standard Package (≤5,000 PSI)';
      const choke = equipment.find(e => e.name === 'Choke Manifold');
      if (choke) choke.specification = '5K PSI';
      technicalNotes.push('Standard pressure rating applied (≤5,000 PSI)');
    } else if (pressure > 5000 && pressure <= 10000) {
      packageLevel = '10K Equipment Required';
      const choke = equipment.find(e => e.name === 'Choke Manifold');
      if (choke) choke.specification = '10K PSI';
      technicalNotes.push('10K-rated equipment selected for high-pressure application');
      flags.push('10K PSI equipment configuration required');
    } else if (pressure > 10000) {
      packageLevel = 'Engineering Review Required (>10K PSI)';
      const choke = equipment.find(e => e.name === 'Choke Manifold');
      if (choke) choke.specification = '15K PSI (Engineering Review)';
      flags.push('⚠️ ENGINEERING REVIEW REQUIRED — Pressure exceeds 10,000 PSI');
      technicalNotes.push('Pressure exceeds 10K PSI — requires engineering review and custom design');
    }
  }

  // ─── SAND LOGIC ──────────────────────────────────────────────────────────
  if (state.sandProduction === 'medium') {
    equipment.push(
      { name: 'Sand Filter', specification: 'Medium Capacity', category: 'primary', dailyRate: 350 },
    );
    technicalNotes.push('Sand filter added for medium sand production');
  } else if (state.sandProduction === 'high') {
    equipment.push(
      { name: 'Desander', specification: 'High Capacity', category: 'primary', dailyRate: 500 },
      { name: 'Sand Filter', specification: 'High Capacity', category: 'primary', dailyRate: 350 },
      { name: 'Debris Catcher', specification: 'Inline', category: 'primary', dailyRate: 250 },
    );
    flags.push('Full sand management system required (Desander + Filter + Debris Catcher)');
    technicalNotes.push('Full sand management system deployed for high sand production');
  }

  // ─── GAS LOGIC ───────────────────────────────────────────────────────────
  const gasRate = parseFloat(state.gasRate) || 0;
  if (gasRate > 0 && gasRate <= 30) {
    const flare = equipment.find(e => e.name === 'Flare System');
    if (flare) flare.specification = 'Standard Burner';
    technicalNotes.push(`Gas rate ${gasRate} MMSCFD — standard flare system adequate`);
  } else if (gasRate > 30 && gasRate <= 50) {
    const flare = equipment.find(e => e.name === 'Flare System');
    if (flare) {
      flare.specification = `Upgraded Flare — ${gasRate} MMSCFD`;
      flare.dailyRate = 1400;
    }
    flags.push('Flare system upgraded for gas rate >30 MMSCFD');
    technicalNotes.push(`Gas rate ${gasRate} MMSCFD — upgraded flare system required`);
  } else if (gasRate > 50) {
    const flare = equipment.find(e => e.name === 'Flare System');
    if (flare) {
      flare.specification = `Engineering Design Required — ${gasRate} MMSCFD`;
      flare.dailyRate = 2000;
    }
    flags.push('⚠️ ENGINEERING DESIGN REQUIRED — Gas rate exceeds 50 MMSCFD');
    technicalNotes.push(`Gas rate ${gasRate} MMSCFD — custom flare engineering design required`);
  }

  // ─── H2S LOGIC ───────────────────────────────────────────────────────────
  if (state.h2sPresence === 'yes') {
    equipment.push(
      { name: 'Safety System', specification: 'SCBA, Gas Detectors, Wind Socks', category: 'safety', dailyRate: 700 },
      { name: 'HSE Controls', specification: 'Sour Service Protocol', category: 'safety', dailyRate: 400 },
      { name: 'Special Materials', specification: 'NACE MR0175 Compliant', category: 'safety', dailyRate: 600 },
    );
    flags.push('⚠️ H₂S PRESENT — Full safety system and special materials required');
    technicalNotes.push('H₂S environment — NACE-compliant materials and safety systems mandatory');
  }

  // ─── LOCATION LOGIC (Remote) ─────────────────────────────────────────────
  if (state.locationType === 'remote' || state.locationType === 'desert') {
    equipment.push(
      { name: 'Generator', specification: 'Self-Contained Power Supply', category: 'logistics', dailyRate: 500 },
      { name: 'Logistics Fleet', specification: 'Transport & Support Vehicles', category: 'logistics', dailyRate: 800 },
      { name: 'Site Support', specification: 'Camp & Welfare', category: 'logistics', dailyRate: 600 },
    );
    technicalNotes.push('Remote/Desert location — generator, logistics fleet, and site support included');
  }

  // ─── SERVICE MODEL LOGIC ─────────────────────────────────────────────────
  let serviceModelNote = '';
  if (state.serviceModel === 'rental') {
    serviceModelNote = 'Equipment only — Client provides crew and supervision';
    technicalNotes.push('Rental mode — equipment supply only');
  } else if (state.serviceModel === 'full-service') {
    serviceModelNote = 'Equipment + Crew + Supervision included';
    equipment.push(
      { name: 'Crew', specification: 'Trained Operators (2-4 persons)', category: 'support', dailyRate: 900 },
      { name: 'Supervision', specification: 'Dedicated Supervisor', category: 'support', dailyRate: 850 },
    );
    technicalNotes.push('Full service mode — crew and supervision included');
  }

  // ─── OFFSHORE LOGIC ──────────────────────────────────────────────────────
  if (state.environment === 'offshore') {
    flags.push('Offshore deployment — Marine-rated equipment and logistics coordination required');
    technicalNotes.push('Offshore environment — marine certification and logistics coordination needed');
  }

  // ─── OIL RATE LOGIC ──────────────────────────────────────────────────────
  const oilRate = parseFloat(state.oilRate) || 0;
  if (oilRate > 0) {
    const sep = equipment.find(e => e.name === 'Test Separator');
    if (sep) {
      if (oilRate <= 5000) {
        sep.specification = 'Standard Capacity (≤5,000 BOPD)';
      } else if (oilRate <= 20000) {
        sep.specification = 'High Capacity (5K-20K BOPD)';
        sep.dailyRate = 2400;
      } else {
        sep.specification = 'Maximum Capacity (>20K BOPD — Engineering Review)';
        sep.dailyRate = 3200;
        flags.push('⚠️ Flow rate exceeds 20,000 BOPD — Separator sizing review required');
      }
    }
  }

  // ─── PRICING ENGINE ──────────────────────────────────────────────────────
  const pricing = calculatePricing(equipment, state);

  return {
    packageLevel,
    equipmentList: equipment,
    flags,
    serviceModelNote,
    technicalNotes,
    pricing,
  };
}

// ─── PRICING CALCULATION (INTERNAL) ─────────────────────────────────────────

function calculatePricing(equipment: EquipmentItem[], state: RFQState): PricingEstimate {
  const days = getDurationDays(state);

  // Equipment cost: sum of daily rates × duration
  const totalDailyRate = equipment.reduce((sum, item) => sum + item.dailyRate, 0);
  const equipmentMin = totalDailyRate * days * 0.85;
  const equipmentMax = totalDailyRate * days * 1.15;

  // Crew cost
  let crewMin = 0;
  let crewMax = 0;
  if (state.serviceModel === 'full-service') {
    const dailyCrew = CREW_RATES.supervisor + (CREW_RATES.operator * 2) + CREW_RATES.hse;
    crewMin = dailyCrew * days * 0.9;
    crewMax = dailyCrew * days * 1.1;
  }

  // Mobilization
  let mobMin = MOBILIZATION_COSTS.local.min;
  let mobMax = MOBILIZATION_COSTS.local.max;
  if (state.locationType === 'remote' || state.locationType === 'desert') {
    mobMin = MOBILIZATION_COSTS.remote.min;
    mobMax = MOBILIZATION_COSTS.remote.max;
  }
  if (state.country === 'other' || state.environment === 'offshore') {
    mobMin = MOBILIZATION_COSTS.crossBorder.min;
    mobMax = MOBILIZATION_COSTS.crossBorder.max;
  }

  // Subtotal
  const subtotalMin = equipmentMin + crewMin + mobMin;
  const subtotalMax = equipmentMax + crewMax + mobMax;

  // Contingency 10-20%
  const contingencyMin = subtotalMin * 0.10;
  const contingencyMax = subtotalMax * 0.20;

  // Total
  const totalMin = Math.round((subtotalMin + contingencyMin) / 1000) * 1000;
  const totalMax = Math.round((subtotalMax + contingencyMax) / 1000) * 1000;

  return {
    equipmentCostRange: [Math.round(equipmentMin), Math.round(equipmentMax)],
    crewCostRange: [Math.round(crewMin), Math.round(crewMax)],
    mobilizationRange: [mobMin, mobMax],
    contingencyRange: [Math.round(contingencyMin), Math.round(contingencyMax)],
    totalRange: [totalMin || 5000, totalMax || 15000],
  };
}

// ─── FORMAT FUNCTIONS ────────────────────────────────────────────────────────

export function formatRuleEngineOutput(output: RuleEngineOutput): string {
  let text = `Recommended Package: ${output.packageLevel}\n\n`;
  text += 'Equipment Configuration:\n';
  output.equipmentList.forEach(item => {
    text += `• ${item.name} — ${item.specification}\n`;
  });
  if (output.serviceModelNote) {
    text += `\nService Model: ${output.serviceModelNote}\n`;
  }
  if (output.technicalNotes.length > 0) {
    text += '\nTechnical Notes:\n';
    output.technicalNotes.forEach(note => {
      text += `• ${note}\n`;
    });
  }
  if (output.flags.length > 0) {
    text += '\nOperational Flags:\n';
    output.flags.forEach(flag => {
      text += `• ${flag}\n`;
    });
  }
  return text;
}

export function formatRFQData(state: RFQState): string {
  const serviceLabelMap: Record<string, string> = {
    'surface-well-testing': 'Surface Well Testing',
    'pressure-control': 'Pressure Control Equipment',
    'stimulation-nitrogen': 'Stimulation & Nitrogen',
    'pumping-hydrotesting': 'Pumping & Hydrotesting',
    'equipment-rental': 'Equipment Rental',
    'chemicals-supply': 'Chemicals Supply',
    'fabrication-skid': 'Fabrication & Skid Assembly',
    'logistics-transport': 'Logistics & Transportation',
    'engineering-services': 'Engineering Services',
    'ai-systems': 'AI Systems & Automation',
    'spare-parts': 'Spare Parts & Consumables',
    'solid-control': 'Solid Control & Waste Management',
    'downhole-fiber': 'Downhole Fiber Optics',
    'pumping-solutions': 'Pumping Solutions',
    'flame-detection': 'Flame Detection Systems',
  };

  let text = '';
  text += `Service Type: ${serviceLabelMap[state.service] || state.service}\n`;
  if (state.wellheadPressure) text += `Wellhead Pressure: ${state.wellheadPressure} PSI\n`;
  if (state.oilRate) text += `Oil Rate: ${state.oilRate} BOPD\n`;
  if (state.gasRate) text += `Gas Rate: ${state.gasRate} MMSCFD\n`;
  if (state.temperature) text += `Temperature: ${state.temperature}°C\n`;
  if (state.sandProduction) text += `Sand Production: ${state.sandProduction}\n`;
  if (state.h2sPresence) text += `H₂S Presence: ${state.h2sPresence === 'yes' ? 'Yes' : 'No'}\n`;
  if (state.environment) text += `Environment: ${state.environment === 'onshore' ? 'Onshore' : 'Offshore'}\n`;
  if (state.duration) text += `Duration: ${state.duration}\n`;
  if (state.serviceModel) text += `Service Model: ${state.serviceModel === 'rental' ? 'Rental' : 'Full Service'}\n`;
  if (state.country) text += `Country: ${state.country}\n`;
  if (state.locationType) text += `Location Type: ${state.locationType}\n`;
  if (state.timeline) text += `Timeline: ${state.timeline}\n`;
  if (state.additionalNotes) text += `Additional Notes: ${state.additionalNotes}\n`;
  return text;
}
